import type { QueryClient } from '@tanstack/react-query';
import type {
  GetAuditSummaryCommandOutput,
  QuestionResponseDetails,
  SubQuestionResponseEntry,
} from '@amzn/gbsa-helios-typescript-client';
import type { CacheSnapshot } from './patchAuditSummaryCache';
import type { NaReasonOverride } from './preserveResponseFields';
import {
  EMPTY_RESPONSE,
  auditSummaryPredicate,
  buildNextCacheData,
} from './patchAuditCachePlumbing';

/**
 * Optimistic cache patches for FIELD saves (comment / naReason /
 * mediaIds). Without these, a full-PATCH write firing right after a
 * field save would preserve from stale cache and silently clear the
 * just-saved field. No metrics diff — fields don't change results.
 */

export interface QuestionCommentsPatch {
  /** Draft snapshot keyed by parent questionId / subQuestionId;
   *  blank string = clear. */
  readonly commentSnapshot: Readonly<Record<string, string>>;
}

/** Patch cached comments on the parent and every existing sub entry. */
export function snapshotAndPatchAuditQuestionComments(
  queryClient: QueryClient,
  auditId: string,
  questionId: string,
  patch: QuestionCommentsPatch,
): CacheSnapshot[] {
  const matchingQueries = queryClient.getQueriesData<GetAuditSummaryCommandOutput>({
    predicate: auditSummaryPredicate(auditId),
  });
  const snapshots: CacheSnapshot[] = [];

  for (const [queryKey, cachedData] of matchingQueries) {
    const nextData = computeQuestionCommentsPatch(cachedData, questionId, patch);
    if (!nextData) continue;
    snapshots.push({ queryKey, data: cachedData });
    queryClient.setQueryData(queryKey, nextData);
  }
  return snapshots;
}

function computeQuestionCommentsPatch(
  cachedData: GetAuditSummaryCommandOutput | undefined,
  questionId: string,
  patch: QuestionCommentsPatch,
): GetAuditSummaryCommandOutput | null {
  if (!cachedData?.questions || !cachedData.audit) return null;
  const targetIndex = cachedData.questions.findIndex(
    (q) => q.questionDetails.questionId === questionId,
  );
  if (targetIndex < 0) return null;
  const targetQuestion = cachedData.questions.at(targetIndex);
  if (!targetQuestion) return null;

  const previousMain = targetQuestion.responseDetails?.mainQuestionResponse;
  const parentComment = normalizeCommentSlot(patch.commentSnapshot, questionId);
  const nextMain = buildMainWithComment(previousMain, parentComment);
  const previousSubs = targetQuestion.responseDetails?.subQuestionResponses ?? [];
  const nextSubs = previousSubs.map((entry) =>
    applyCommentToSubEntry(entry, patch.commentSnapshot),
  );

  return buildNextCacheData(cachedData, targetIndex, nextMain, nextSubs, undefined);
}

function buildMainWithComment(
  previous: QuestionResponseDetails | undefined,
  comment: string | undefined,
): QuestionResponseDetails {
  const base = previous ?? EMPTY_RESPONSE;
  if (comment === undefined) {
    // Drop the field (server-cleared), don't leave `undefined` in place.
    const next: Partial<QuestionResponseDetails> = { ...base };
    delete next.responseComment;
    return next as QuestionResponseDetails;
  }
  return { ...base, responseComment: comment };
}

function applyCommentToSubEntry(
  entry: SubQuestionResponseEntry,
  snapshot: Readonly<Record<string, string>>,
): SubQuestionResponseEntry {
  const next = normalizeCommentSlot(snapshot, entry.subQuestionId);
  const nextResponse: Partial<QuestionResponseDetails> = { ...entry.response };
  if (next === undefined) {
    delete nextResponse.responseComment;
  } else {
    nextResponse.responseComment = next;
  }
  return { ...entry, response: nextResponse as QuestionResponseDetails };
}

/** Blank / whitespace-only → undefined (cleared); non-empty → trimmed. */
function normalizeCommentSlot(
  snapshot: Readonly<Record<string, string>>,
  key: string,
): string | undefined {
  // eslint-disable-next-line security/detect-object-injection
  const raw = snapshot[key];
  if (raw === undefined) return undefined;
  const trimmed = raw.trim();
  return trimmed === '' ? undefined : trimmed;
}

export interface QuestionNaReasonsPatch {
  /** Tri-state per slot: absent = preserve, null = clear,
   *  {reasonId,label} = set (only the label lands in cache — the
   *  read shape has no id). */
  readonly naReasonSnapshot: Readonly<Record<string, NaReasonOverride>>;
}

/** Patch cached NA-reason labels on the parent and every existing sub entry. */
export function snapshotAndPatchAuditQuestionNaReasons(
  queryClient: QueryClient,
  auditId: string,
  questionId: string,
  patch: QuestionNaReasonsPatch,
): CacheSnapshot[] {
  const matchingQueries = queryClient.getQueriesData<GetAuditSummaryCommandOutput>({
    predicate: auditSummaryPredicate(auditId),
  });
  const snapshots: CacheSnapshot[] = [];

  for (const [queryKey, cachedData] of matchingQueries) {
    const nextData = computeQuestionNaReasonsPatch(cachedData, questionId, patch);
    if (!nextData) continue;
    snapshots.push({ queryKey, data: cachedData });
    queryClient.setQueryData(queryKey, nextData);
  }
  return snapshots;
}

function computeQuestionNaReasonsPatch(
  cachedData: GetAuditSummaryCommandOutput | undefined,
  questionId: string,
  patch: QuestionNaReasonsPatch,
): GetAuditSummaryCommandOutput | null {
  if (!cachedData?.questions || !cachedData.audit) return null;
  const targetIndex = cachedData.questions.findIndex(
    (q) => q.questionDetails.questionId === questionId,
  );
  if (targetIndex < 0) return null;
  const targetQuestion = cachedData.questions.at(targetIndex);
  if (!targetQuestion) return null;

  const previousMain = targetQuestion.responseDetails?.mainQuestionResponse;
  const parentAction = readNaReasonSlot(patch.naReasonSnapshot, questionId);
  const nextMain = buildMainWithNaReason(previousMain, parentAction);
  const previousSubs = targetQuestion.responseDetails?.subQuestionResponses ?? [];
  const nextSubs = previousSubs.map((entry) =>
    applyNaReasonToSubEntry(entry, patch.naReasonSnapshot),
  );

  return buildNextCacheData(cachedData, targetIndex, nextMain, nextSubs, undefined);
}

type NaReasonSlotAction =
  | { readonly kind: 'preserve' }
  | { readonly kind: 'clear' }
  | { readonly kind: 'set'; readonly label: string };

function readNaReasonSlot(
  snapshot: Readonly<Record<string, NaReasonOverride>>,
  key: string,
): NaReasonSlotAction {
  // eslint-disable-next-line security/detect-object-injection
  const raw = snapshot[key];
  if (raw === undefined) return { kind: 'preserve' };
  if (raw === null) return { kind: 'clear' };
  return { kind: 'set', label: raw.label };
}

function buildMainWithNaReason(
  previous: QuestionResponseDetails | undefined,
  action: NaReasonSlotAction,
): QuestionResponseDetails {
  const base = previous ?? EMPTY_RESPONSE;
  if (action.kind === 'preserve') return base;
  if (action.kind === 'clear') {
    const next: Partial<QuestionResponseDetails> = { ...base };
    delete next.naReason;
    return next as QuestionResponseDetails;
  }
  return { ...base, naReason: action.label };
}

function applyNaReasonToSubEntry(
  entry: SubQuestionResponseEntry,
  snapshot: Readonly<Record<string, NaReasonOverride>>,
): SubQuestionResponseEntry {
  const action = readNaReasonSlot(snapshot, entry.subQuestionId);
  if (action.kind === 'preserve') return entry;
  const nextResponse: Partial<QuestionResponseDetails> = { ...entry.response };
  if (action.kind === 'clear') {
    delete nextResponse.naReason;
  } else {
    nextResponse.naReason = action.label;
  }
  return { ...entry, response: nextResponse as QuestionResponseDetails };
}

export interface QuestionMediaIdsPatch {
  /** Absent = parent slot; present = the target sub slot. */
  readonly subQuestionId?: string | undefined;
  /** Complete effective id list (persisted ∪ new) just committed. */
  readonly mediaIds: readonly string[];
}

/**
 * Patch cached mediaIds for one slot after a media save — clears the
 * flyout's missing-attachment violation and lets useMediaDraft's
 * baseline guard see the ids as persisted.
 */
export function snapshotAndPatchAuditQuestionMediaIds(
  queryClient: QueryClient,
  auditId: string,
  questionId: string,
  patch: QuestionMediaIdsPatch,
): CacheSnapshot[] {
  const matchingQueries = queryClient.getQueriesData<GetAuditSummaryCommandOutput>({
    predicate: auditSummaryPredicate(auditId),
  });
  const snapshots: CacheSnapshot[] = [];

  for (const [queryKey, cachedData] of matchingQueries) {
    const nextData = computeQuestionMediaIdsPatch(cachedData, questionId, patch);
    if (!nextData) continue;
    snapshots.push({ queryKey, data: cachedData });
    queryClient.setQueryData(queryKey, nextData);
  }
  return snapshots;
}

function computeQuestionMediaIdsPatch(
  cachedData: GetAuditSummaryCommandOutput | undefined,
  questionId: string,
  patch: QuestionMediaIdsPatch,
): GetAuditSummaryCommandOutput | null {
  if (!cachedData?.questions || !cachedData.audit) return null;
  const targetIndex = cachedData.questions.findIndex(
    (q) => q.questionDetails.questionId === questionId,
  );
  if (targetIndex < 0) return null;
  const targetQuestion = cachedData.questions.at(targetIndex);
  if (!targetQuestion) return null;

  const previousMain = targetQuestion.responseDetails?.mainQuestionResponse;
  const previousSubs = targetQuestion.responseDetails?.subQuestionResponses ?? [];

  if (patch.subQuestionId === undefined) {
    const nextMain: QuestionResponseDetails = {
      ...(previousMain ?? EMPTY_RESPONSE),
      mediaIds: [...patch.mediaIds],
    };
    return buildNextCacheData(
      cachedData,
      targetIndex,
      nextMain,
      [...previousSubs],
      undefined,
    );
  }

  const nextSubs = applyMediaIdsToSubEntries(
    previousSubs,
    patch.subQuestionId,
    patch.mediaIds,
  );
  const nextMain = previousMain ?? EMPTY_RESPONSE;
  return buildNextCacheData(cachedData, targetIndex, nextMain, nextSubs, undefined);
}

/** Write ids onto the target sub; synthesize the entry for attach-before-answer. */
function applyMediaIdsToSubEntries(
  existing: readonly SubQuestionResponseEntry[],
  targetSubQuestionId: string,
  mediaIds: readonly string[],
): SubQuestionResponseEntry[] {
  const targetIndex = existing.findIndex(
    (entry) => entry.subQuestionId === targetSubQuestionId,
  );
  if (targetIndex >= 0) {
    return existing.map((entry, i) =>
      i === targetIndex
        ? { ...entry, response: { ...entry.response, mediaIds: [...mediaIds] } }
        : entry,
    );
  }
  return [
    ...existing,
    {
      subQuestionId: targetSubQuestionId,
      response: {
        ...EMPTY_RESPONSE,
        mediaIds: [...mediaIds],
      },
    },
  ];
}
